/**
 * API helpers — all backend calls go through here.
 * Uses REACT_APP_BACKEND_URL from env. Never hardcode.
 */
const BASE = process.env.REACT_APP_BACKEND_URL;

// Phase 17.2 — re-exported so the wizard flows (Step1/Step6/shell) can build
// URLs without re-deriving from env. Editorial code paths keep using the
// helper functions below, untouched.
export const API = BASE;

// Empty BASE is intentional: relative paths (`/api/...`) route to whichever
// origin serves the frontend (preview OR production) via the K8s ingress. A
// blank REACT_APP_BACKEND_URL is therefore the DEFAULT correct configuration.
// We only warn if BASE is explicitly set to a truthy value that isn't a URL.
if (BASE && !/^https?:\/\//i.test(BASE)) {
    // eslint-disable-next-line no-console
    console.error("REACT_APP_BACKEND_URL is set but is not an http(s) URL:", BASE);
}

function api(path) {
    return `${BASE}${path}`;
}

// All requests are sent with credentials so the session cookie flows.
//
// Issue #31 — the single sanctioned way to reach the backend. Exported so
// call sites that don't map cleanly onto one of the named helpers below
// (a blob download, a bespoke polling loop, etc.) can still avoid
// hand-rolling `fetch(\`${BACKEND_URL}...\`, {credentials: "include", ...})`
// themselves. New code should prefer a named helper when one exists or
// fits; add one here rather than reaching for baseFetch when the call
// site is a common, reusable shape.
export const baseFetch = (path, opts = {}) =>
    fetch(api(path), { credentials: "include", ...opts });

// FastAPI's `detail` is a string for HTTPException but a LIST of
// `{loc, msg, type}` objects for a 422 validation error. `new Error(list)`
// stringifies to "[object Object]", which is what the user used to see.
function formatErrorDetail(detail) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        const parts = detail.map((d) => {
            if (typeof d === "string") return d;
            const msg = d && (d.msg || d.message);
            if (!msg) return JSON.stringify(d);
            // Drop the leading "body"/"query" so it reads "decision_text: field required".
            const where = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body" && x !== "query").join(".") : "";
            return where ? `${where}: ${msg}` : msg;
        });
        return parts.join("; ");
    }
    if (detail && typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
    return String(detail);
}

export async function jsonOrThrow(res) {
    if (!res.ok) {
        let detail = "";
        try {
            const body = await res.json();
            detail = formatErrorDetail(body.detail || JSON.stringify(body));
        } catch (_) {
            detail = `HTTP ${res.status}`;
        }
        const err = new Error(detail);
        err.status = res.status;
        throw err;
    }
    return res.json();
}

// ---- Auth ----
export async function getMe() {
    const res = await baseFetch("/api/auth/me");
    if (res.status === 401) return null;
    return jsonOrThrow(res);
}

export async function processSession(session_id) {
    const res = await baseFetch("/api/auth/process-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id }),
    });
    return jsonOrThrow(res);
}

export async function logout() {
    const res = await baseFetch("/api/auth/logout", { method: "POST" });
    return jsonOrThrow(res);
}

// ---- Analyses ----
// Issue #142 — `createAnalysis()` (the synchronous POST /api/analyses)
// was removed here. That route runs the RETIRED engine: it has no
// CORE_ALOG_ENABLED check and cannot have one, because core_alog takes
// 30-90s and would blow the gateway timeout that Phase 15.2's async
// route exists to avoid. Every caller now uses `createAnalysisAsync()`
// below and hands off to the /analyses/{id}/preparing loader.
//
// Do not reintroduce a synchronous create helper. If a caller needs the
// full Analysis object, poll for it — the id arrives in under a second.


// ──────────────────────────────────────────────────────────────────────
// Fix #5 (2026-07-07) — Async analysis creation
// `POST /api/analyses/async` returns HTTP 202 in <1 s with the id + a
// `pending` sentinel. The full pipeline (core + enrichment) runs in a
// background task; the `/analyses/{id}/preparing` route polls the doc
// until `enrichment_status === "completed"` (or `"failed"`, or the
// hard cap is hit).
// ──────────────────────────────────────────────────────────────────────
export async function createAnalysisAsync(payloadIn, { signal } = {}) {
    // Same body shape as `createAnalysis` — the async handler shares the
    // `AnalysisCreate` Pydantic model on the backend so wizard payloads
    // work unchanged. Uses fetch directly (not baseFetch) so we can set
    // an Idempotency-Key without disturbing the shared header defaults.
    const {
        decision_text,
        seed,
        decision_spec = null,
        refined_from = null,
        extracted_spec_before_override = null,
        fine_tuned = false,
        wizard = null,
        workspace_id = null,
    } = payloadIn || {};
    const body = { decision_text, seed };
    if (decision_spec) body.decision_spec = decision_spec;
    if (refined_from) body.refined_from = refined_from;
    if (extracted_spec_before_override) body.extracted_spec_before_override = extracted_spec_before_override;
    if (fine_tuned) body.fine_tuned = true;
    if (wizard) body.wizard = wizard;
    if (workspace_id) body.workspace_id = workspace_id;
    const json = await jsonOrThrow(
        await baseFetch("/api/analyses/async", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            signal,
        })
    );
    // Issue #151 — the fine-tune ping used to live only in the synchronous
    // `createAnalysis()`. The wizard became the canonical intake in Phase
    // 17.2 and submits here, so the ping never fired for the one surface
    // that actually sets `fine_tuned` — `top_fields_edited`,
    // `distinct_fine_tune_users` and `last_event_at` on the admin panel
    // have been empty ever since. Firing it here rather than in the page
    // means any future caller of this helper gets it without rewiring,
    // which was the intent of its original placement.
    const createdId = json && (json.analysis_id || json.id);
    if (createdId && fine_tuned === true) {
        try {
            const fields_edited = diffSpecFields(
                decision_spec || {},
                extracted_spec_before_override || {},
            );
            // Don't await — UI shouldn't block on telemetry.
            pingFineTuneSubmitted({
                analysis_id: createdId,
                fields_edited,
                submission_source: refined_from ? "analyses-rerun" : "analyses-new",
            }).catch(() => {});
        } catch (_) {/* never block create on analytics */}
    }
    return json;
}

// Issue #142 — kept on purpose when `createAnalysis()` was removed.
// `diffSpecFields()` computes `fields_edited` for the fine-tune analytics
// ping, and the async create path needs it (issue #151 wires the ping into
// `createAnalysisAsync()`). Deleting it would not fail loudly there: the call
// sits inside a try/catch that swallows the resulting ReferenceError, so the
// ping would silently never fire. Exported so it cannot be flagged unused.
export function diffSpecFields(edited, extracted) {
    if (!edited || !extracted) return [];
    const keys = [
        "action", "actor", "scale", "horizon_months", "reversibility",
        "specialization", "domains_affected",
    ];
    const out = [];
    for (const k of keys) {
        const a = edited[k];
        const b = extracted[k];
        if (Array.isArray(a) || Array.isArray(b)) {
            const sa = JSON.stringify([...(a || [])].sort());
            const sb = JSON.stringify([...(b || [])].sort());
            if (sa !== sb) out.push(k);
        } else if ((a ?? null) !== (b ?? null)) {
            out.push(k);
        }
    }
    return out;
}

// Phase 14 W3 — Analytics ping. Silent on failure (best-effort telemetry).
export async function pingFineTuneSubmitted({ analysis_id, fields_edited, submission_source }) {
    return jsonOrThrow(
        await baseFetch("/api/analytics/fine-tune-submitted", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ analysis_id, fields_edited, submission_source }),
        })
    );
}

// Phase 14 W5 — Short links.
// Phase 17.7 — Accepts either a string target (legacy) OR an object
// `{ target, single_use?, expires_at? }` for share-to-board.
export async function createShortLink(targetOrOpts) {
    const body = typeof targetOrOpts === "string"
        ? { target: targetOrOpts }
        : {
              target: targetOrOpts.target,
              ...(targetOrOpts.single_use ? { single_use: true } : {}),
              ...(targetOrOpts.expires_at ? { expires_at: targetOrOpts.expires_at } : {}),
          };
    return jsonOrThrow(
        await baseFetch("/api/short-links", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        })
    );
}

export async function resolveShortLink(slug) {
    return jsonOrThrow(await baseFetch(`/api/short-links/resolve/${slug}`));
}

// Phase 16 W4 — Daily news headlines for /analyses/new quick-start chips.
export async function getTodaysHeadlines({ refresh = false } = {}) {
    const qs = refresh ? "?refresh=true" : "";
    return jsonOrThrow(await baseFetch(`/api/headlines/today${qs}`));
}

export async function getAnalysis(id, { token = null } = {}) {
    const suffix = token ? `?token=${encodeURIComponent(token)}` : "";
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}${suffix}`));
}

// Phase 19.6 — sharing controls.
export async function createAuditLink(id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/audit-link`, { method: "POST" }));
}
// Issue #33 — invalidates every audit-link token minted so far for this
// analysis; a new createAuditLink() call afterward is unaffected.
export async function revokeAuditLink(id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/audit-link/revoke`, { method: "POST" }));
}
// Issue #211 (R6 story 1.15) — re-run a pre-R6 analysis on the current
// engine. Creates a NEW analysis and returns its id; the original is left
// exactly as it was, seal included, so this is additive and never destructive.
export async function rerunAnalysis(id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/rerun-on-current-engine`, { method: "POST" }));
}

export async function patchAnalysisVisibility(id, visibility) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visibility }),
    }));
}

export async function listAnalyses({
    skip = 0,
    limit = 20,
    mine = false,
    q = null,
    domain = null,
    owner = null,
    sort = null,
    fine_tuned = null,    // Phase 14 W4 — filter to fine-tune-edited analyses
} = {}) {
    const params = new URLSearchParams({ skip, limit });
    if (mine) params.set("mine", "true");
    if (q && q.trim()) params.set("q", q.trim());
    if (domain) params.set("domain", domain);
    if (owner) params.set("owner", owner);
    if (sort) params.set("sort", sort);
    if (fine_tuned === true) params.set("fine_tuned", "true");
    return jsonOrThrow(await baseFetch(`/api/analyses?${params}`));
}

export async function deleteAnalysis(id) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${id}`, { method: "DELETE" })
    );
}

export async function getAnalysisAudit(id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/audit`));
}

// ---- Chat ----
export async function getChat(id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/chat`));
}

export async function postChat(id, message) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${id}/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message }),
        })
    );
}

// ---- Validation ----
export async function getValidationQueue() {
    return jsonOrThrow(await baseFetch("/api/validation/queue"));
}

export async function postValidation({ analysis_id, factor_name, decision, override_score, note }) {
    return jsonOrThrow(
        await baseFetch("/api/validation", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                analysis_id, factor_name, decision,
                override_score: override_score ?? null,
                note: note || null,
            }),
        })
    );
}

export async function listValidations(analysisId) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysisId}/validations`)
    );
}

export async function uploadDocumentExtract(file) {
    const form = new FormData();
    form.append("file", file);
    const res = await baseFetch("/api/documents/extract", {
        method: "POST",
        body: form,
    });
    return jsonOrThrow(res);
}

// ---- Enhance prompt (Phase 5) ----
export async function enhancePrompt(prompt) {
    const res = await baseFetch("/api/enhance-prompt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
    });
    return jsonOrThrow(res);
}

export async function recordEnhanceSubmit({ original_prompt, final_submitted_text, was_reverted }) {
    try {
        await baseFetch("/api/enhance-submit-analytics", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ original_prompt, final_submitted_text, was_reverted }),
        });
    } catch (_) { /* fire-and-forget */ }
}

export const BACKEND_URL = BASE;

// Phase H1 — visible-horizons PATCH. Idempotent; server rejects empty
// array + unknown horizon ids with 422. Accepts hyphenated legacy ids
// (`near-term`) and normalises to underscore form.
export async function patchVisibleHorizons(analysis_id, visible_horizons) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysis_id}/visible-horizons`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ visible_horizons }),
        })
    );
}

// Phase H2 — visible-domains PATCH. Same contract as H1's horizons
// endpoint: idempotent, at-least-one-visible guard on the server,
// hyphenated legacy ids (e.g. "public-health") normalised to
// underscore form. Body: `{ visible_domains: string[] }`.
export async function patchVisibleDomains(analysis_id, visible_domains) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysis_id}/visible-domains`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ visible_domains }),
        })
    );
}

// ============================================================================
// Phase 7 — Wave 2 endpoints
// ============================================================================

export async function getManifest(id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/manifest`));
}

export async function compareAnalyses(analysis_ids) {
    return jsonOrThrow(await baseFetch(`/api/compare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analysis_ids }),
    }));
}

// Issue #161 — a share-link recipient is anonymous; without forwarding
// the token this 404s and the panel renders empty for them.
export async function listAnnotations(id, { token = null } = {}) {
    const suffix = token ? `?token=${encodeURIComponent(token)}` : "";
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/annotations${suffix}`));
}

export async function createAnnotation(id, { element_ref, note }) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/annotations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ element_ref, note }),
    }));
}

export async function deleteAnnotation(id, annotation_id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/annotations/${annotation_id}`, {
        method: "DELETE",
    }));
}

export function embedUrl(id) {
    return `${BASE}/api/embed/verify/${id}`;
}

// ============================================================================
// Phase 8 — Workspaces + RBAC
// ============================================================================
export async function listWorkspaces() {
    return jsonOrThrow(await baseFetch(`/api/workspaces`));
}
export async function createWorkspace({ name, default_visibility = "workspace" }) {
    return jsonOrThrow(await baseFetch(`/api/workspaces`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, default_visibility }),
    }));
}
export async function getWorkspace(id) {
    return jsonOrThrow(await baseFetch(`/api/workspaces/${id}`));
}
export async function patchWorkspace(id, patch) {
    return jsonOrThrow(await baseFetch(`/api/workspaces/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
    }));
}
export async function deleteWorkspace(id) {
    return jsonOrThrow(await baseFetch(`/api/workspaces/${id}`, { method: "DELETE" }));
}
export async function inviteMember(ws_id, { email, role }) {
    return jsonOrThrow(await baseFetch(`/api/workspaces/${ws_id}/invitations`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
    }));
}
export async function acceptInvite(token) {
    return jsonOrThrow(await baseFetch(`/api/workspaces/accept-invite`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
    }));
}
export async function updateMemberRole(ws_id, user_id, role) {
    return jsonOrThrow(await baseFetch(`/api/workspaces/${ws_id}/members/${user_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
    }));
}
export async function removeMember(ws_id, user_id) {
    return jsonOrThrow(await baseFetch(`/api/workspaces/${ws_id}/members/${user_id}`, {
        method: "DELETE",
    }));
}

// Phase 8 — Multi-reviewer validation
export async function listFindings(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "" && v !== false) qs.set(k, v);
    });
    return jsonOrThrow(await baseFetch(`/api/validation/findings?${qs}`));
}
export async function getFinding(id) {
    return jsonOrThrow(await baseFetch(`/api/validation/findings/${id}`));
}
export async function assignReviewers(fid, body) {
    return jsonOrThrow(await baseFetch(`/api/validation/findings/${fid}/assignments`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    }));
}
export async function submitAssessment(fid, body) {
    return jsonOrThrow(await baseFetch(`/api/validation/findings/${fid}/assessments`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    }));
}
export async function escalateFinding(fid) {
    return jsonOrThrow(await baseFetch(`/api/validation/findings/${fid}/escalate`, {
        method: "POST",
    }));
}

// Phase 8 — Outcomes
export async function listOutcomes(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v) qs.set(k, v);
    });
    return jsonOrThrow(await baseFetch(`/api/outcomes?${qs}`));
}
export async function createOutcome(body) {
    return jsonOrThrow(await baseFetch(`/api/outcomes`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    }));
}
export async function deleteOutcome(id) {
    return jsonOrThrow(await baseFetch(`/api/outcomes/${id}`, { method: "DELETE" }));
}
export async function outcomeSummary(window_days = 90) {
    return jsonOrThrow(await baseFetch(`/api/outcomes/summary?window_days=${window_days}`));
}


// ============================================================================
// Phase 17.7 — HITL seal + scenario branching + decision memory + real events
// ============================================================================

// HITL verify-and-seal. Body: { verification_answers, verification_notes, flagged_domains }.
export async function sealAnalysis(id, body) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${id}/seal`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body || {}),
        })
    );
}

// Spawn a child analysis with one structural change. Body: { field, before, after, label }.
export async function branchAnalysis(id, body) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${id}/branch`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        })
    );
}

// Fetch parent + up to 3 branch children for the comparison view.
export async function listBranches(id) {
    return jsonOrThrow(await baseFetch(`/api/analyses/${id}/branches`));
}

// Record a real-world event and spawn an event-driven child branch.
export async function recordRealEvent(id, body) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${id}/real-event`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        })
    );
}

// Decision Memory — top 3 past analyses similar to the wizard's current decision.
export async function wizardSimilarAnalyses({ decision_text, actor = null, scope_kind = null }) {
    return jsonOrThrow(
        await baseFetch(`/api/wizard/similar-analyses`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ decision_text, actor, scope_kind }),
        })
    );
}

// ──────────────────────────────────────────────────────────────────────
// Phase M3 — Question variants (branch a new question on the same
// context, switch canonical, compare two variants side-by-side).
// ──────────────────────────────────────────────────────────────────────
export async function createAnalysisVariant(analysis_id, new_question, { idempotencyKey = null } = {}) {
    const headers = { "Content-Type": "application/json" };
    if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysis_id}/variants`, {
            method: "POST",
            headers,
            body: JSON.stringify({ new_question }),
        })
    );
}

// Phase M4 Item 3 — polling endpoint for the async variant pipeline.
// Returns { variant, status: 'pending' | 'complete' | 'error',
// canonical_variant_id }.
export async function getAnalysisVariant(analysis_id, variant_id) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysis_id}/variants/${variant_id}`)
    );
}

// Phase M4 Item 1 — methodology updates subscribe (mailto capture).
// No auth. Returns { ok, already_subscribed, subscribed_at }.
export async function subscribeMethodologyUpdates(email, source = "updates-page") {
    return jsonOrThrow(
        await baseFetch(`/api/methodology/updates/subscribe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, source }),
        })
    );
}

export async function patchAnalysisCanonical(analysis_id, variant_id) {
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysis_id}/canonical`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ variant_id }),
        })
    );
}

export async function compareAnalysisVariants(analysis_id, variant_a, variant_b) {
    const qs = new URLSearchParams({ a: variant_a, b: variant_b }).toString();
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysis_id}/compare?${qs}`)
    );
}


// ──────────────────────────────────────────────────────────────────────
// Phase CM-2 — Consequence Map v2 (Domain Explorer + Cascade Chains)
// Backend cache lives on the analysis doc under `consequence_map`.
// `getConsequenceMap` never blocks (returns {status:'unavailable'} when
// no cache); `synthesizeConsequenceMap` fires the two-LLM-call pipeline
// and caches the result. Pass `refresh:true` to force regenerate.
// ──────────────────────────────────────────────────────────────────────
export async function getConsequenceMap(analysis_id, { token = null } = {}) {
    const suffix = token ? `?token=${encodeURIComponent(token)}` : "";
    return jsonOrThrow(
        await baseFetch(`/api/analyses/${analysis_id}/consequence-map${suffix}`)
    );
}

export async function synthesizeConsequenceMap(
    analysis_id,
    { refresh = false, timeoutMs = 180_000, pollIntervalMs = 2000 } = {}
) {
    // Fix (2026-07-21 P0) — Async 202 + poll pattern. The backend
    // synthesize endpoint now returns HTTP 202 with `status:"pending"`
    // for a cache-miss and kicks off the two-LLM synthesis in an
    // `asyncio.create_task` background job. The client polls the GET
    // endpoint at `pollIntervalMs` intervals until `status:"cached"`
    // (payload present), `status:"failed"` (error surfaced), or
    // `status:"unavailable"` after the background task ended without
    // writing a payload — see the CM-2 IndexError graceful-degradation
    // path. This replaces the previous long-hold sync request that
    // exceeded the ~30 s edge idle timeout and 504'd every first
    // click on live.
    const suffix = refresh ? "?refresh=true" : "";
    const kickoff = jsonOrThrow(
        await baseFetch(
            `/api/analyses/${analysis_id}/consequence-map/synthesize${suffix}`,
            { method: "POST" },
        )
    );
    // If synchronous cache hit → return immediately with the payload.
    if (kickoff && kickoff.status === "cached" && kickoff.consequence_map) {
        return kickoff;
    }
    // Otherwise the backend returned 202 pending — poll GET until ready.
    const started = Date.now();
    // Small delay before first poll so the background task has a chance
    // to start (avoids an immediate "unavailable" false negative).
    await new Promise((r) => setTimeout(r, pollIntervalMs));
    while (Date.now() - started < timeoutMs) {
        const poll = await getConsequenceMap(analysis_id);
        if (poll?.status === "cached" && poll.consequence_map) {
            return poll;
        }
        if (poll?.status === "failed") {
            const e = new Error(
                poll.error === "analysis_timeout"
                    ? "Consequence map is taking longer than expected. Please retry."
                    : `Consequence map synthesis failed (${poll.error || "unknown"}). Please retry.`
            );
            e.code = poll.error || "cm2_failed";
            throw e;
        }
        // `unavailable` after a POST means the background task ended
        // without writing a payload (e.g. IndexError graceful path).
        // Surface as a soft failure so the UI can show its empty state.
        if (poll?.status === "unavailable") {
            return { status: "unavailable", consequence_map: null };
        }
        // Otherwise `generating` — wait and re-poll.
        await new Promise((r) => setTimeout(r, pollIntervalMs));
    }
    const e = new Error(
        "Consequence map is taking longer than expected. Please retry."
    );
    e.code = "cm2_timeout";
    throw e;
}
